package link.erpango.quip.domain.entities

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * DTOs of `POST api/extract-vehicles/` and `GET api/extract-vehicles/status/` (LLM note
 * extraction). Every field has a default so older and newer backends both parse; the
 * response is validated again on the client before anything reaches a form
 * (see `domain/noteimport/RemoteDraftMapper.kt`).
 */
@Serializable
data class DExtractionRequest(val note: String)

@Serializable
data class DExtractionResponse(
    val vehicles: List<DExtractedVehicle> = emptyList(),
    @SerialName("spare_parts")
    val spareParts: List<DExtractedSparePart> = emptyList(),
    val maintenances: List<DExtractedMaintenance> = emptyList(),
)

@Serializable
data class DExtractedVehicle(
    val brand: String? = null,
    val model: String? = null,
    val year: Int? = null,
    val plate: String? = null,
    val vin: String? = null,
    @SerialName("engine_oil")
    val engineOil: String? = null,
    @SerialName("front_tire_size")
    val frontTireSize: String? = null,
    @SerialName("rear_tire_size")
    val rearTireSize: String? = null,
    @SerialName("recommended_fuel")
    val recommendedFuel: String? = null,
    val color: String? = null,
    val owner: String? = null,
    @SerialName("vehicle_type")
    val vehicleType: String? = null,
    @SerialName("cylinder_count")
    val cylinderCount: Int? = null,
    @SerialName("front_tire_pressure_below_160")
    val frontTirePressureBelow160: Double? = null,
    @SerialName("rear_tire_pressure_below_160")
    val rearTirePressureBelow160: Double? = null,
    @SerialName("front_tire_pressure_above_160")
    val frontTirePressureAbove160: Double? = null,
    @SerialName("rear_tire_pressure_above_160")
    val rearTirePressureAbove160: Double? = null,
    @SerialName("first_registration_date")
    val firstRegistrationDate: String? = null,
    @SerialName("next_itv_date")
    val nextItvDate: String? = null,
    @SerialName("front_bulb_type")
    val frontBulbType: String? = null,
    @SerialName("rear_bulb_type")
    val rearBulbType: String? = null,
    @SerialName("battery_specs")
    val batterySpecs: String? = null,
    @SerialName("coolant_type")
    val coolantType: String? = null,
    @SerialName("fields_confidence")
    val fieldsConfidence: Map<String, String> = emptyMap(),
)

@Serializable
data class DExtractedSparePart(
    @SerialName("category_id")
    val categoryId: String? = null,
    @SerialName("spare_part_id")
    val sparePartId: String? = null,
    val brand: String? = null,
    val model: String? = null,
    val reference: String? = null,
    val quantity: Double? = null,
    val unit: String? = null,
    @SerialName("vehicle_index")
    val vehicleIndex: Int = -1,
    @SerialName("fields_confidence")
    val fieldsConfidence: Map<String, String> = emptyMap(),
)

@Serializable
data class DExtractedMaintenance(
    /** ISO date (YYYY-MM-DD). */
    val date: String? = null,
    val km: Int? = null,
    val cost: Double? = null,
    val description: String? = null,
    val workshop: String? = null,
    val mechanic: String? = null,
    @SerialName("vehicle_index")
    val vehicleIndex: Int = -1,
    @SerialName("fields_confidence")
    val fieldsConfidence: Map<String, String> = emptyMap(),
)

@Serializable
data class DExtractionErrorBody(
    val code: String? = null,
    val message: String? = null,
    @SerialName("retry_after")
    val retryAfter: Int? = null,
)

@Serializable
data class DExtractionStatus(
    /** False when the backend has no LLM provider configured: the AI option is hidden. */
    val enabled: Boolean = false,
    @SerialName("max_note_length")
    val maxNoteLength: Int = 2000,
    @SerialName("daily_limit")
    val dailyLimit: Int = 0,
    @SerialName("remaining_today")
    val remainingToday: Int = 0,
    /** Seconds until a new extraction may succeed; 0 = now. */
    @SerialName("retry_after")
    val retryAfter: Int = 0,
)

/** Why an AI extraction failed, mapped from the backend's stable error codes. */
sealed interface DExtractionError {
    data object NoteTooLong : DExtractionError
    data object EmptyNote : DExtractionError
    data class RateLimited(val retryAfterSeconds: Int) : DExtractionError
    data class DailyQuotaExceeded(val retryAfterSeconds: Int) : DExtractionError
    data class GlobalQuotaExceeded(val retryAfterSeconds: Int) : DExtractionError

    /** No provider configured (501) or an old backend without the endpoint (404). */
    data object Unavailable : DExtractionError
    data object Timeout : DExtractionError

    /** The provider failed (502) or the quota store is down (503). */
    data object ProviderError : DExtractionError

    /** The model output could not be validated (422). */
    data object ExtractionFailed : DExtractionError
    data object Unauthorized : DExtractionError
    data object Network : DExtractionError
    data object Unknown : DExtractionError
}

sealed interface DExtractionOutcome {
    data class Success(val response: DExtractionResponse) : DExtractionOutcome
    data class Failure(val error: DExtractionError) : DExtractionOutcome
}
